import { useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@/components/primitives";
import { useUnseenGrailMatches } from "@/hooks/useFigData";
import { useUnreadInbox } from "@/hooks/useMessages";
import { isDockHidden } from "@/lib/dockVisibility";

type DockItem = {
  to: string;
  label: string;
  icon: (s?: number) => JSX.Element;
  // also "active" for these prefixes — e.g. /you should highlight on /profile/me
  matches?: (path: string) => boolean;
};

const items: DockItem[] = [
  { to: "/home",   label: "Home",   icon: Icon.home,    matches: (p) => p === "/home" },
  { to: "/feed",   label: "Feed",   icon: Icon.sparkle, matches: (p) => p.startsWith("/feed") },
  { to: "/post",   label: "Post",   icon: Icon.camera,  matches: () => false }, // center button
  { to: "/grails", label: "Grails", icon: Icon.grail,   matches: (p) => p.startsWith("/grails") },
  { to: "/you",    label: "You",    icon: Icon.user,    matches: (p) => p === "/you" || p.startsWith("/profile/") },
];

export function BottomDock() {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { count: grailUnseen } = useUnseenGrailMatches();
  const { count: inboxUnread } = useUnreadInbox();

  if (isDockHidden(pathname)) return null;

  return (
    <nav
      aria-label="Primary"
      style={{
        // Fixed to the viewport so the dock stays put while content
        // scrolls. Centered + width-clamped to match the MobileFrame
        // (max 430px) so it doesn't stretch on desktop.
        position: "fixed",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: 0,
        width: "100%",
        maxWidth: 430,
        padding: "8px 14px calc(env(safe-area-inset-bottom, 8px) + 8px)",
        // Use --paper-2 (theme's slightly-darker paper token) for a
        // visibly themed strip per theme. The paper-bg class was previously
        // applied here, but at <70px height the kraft/butter/velvet
        // patterns rendered as essentially nothing (their rules and noise
        // tiles are sized for full-page surfaces), leaving the dock as a
        // flat --paper rectangle that didn't read as "themed". Solid
        // paper-2 sidesteps the small-scale pattern problem and gives the
        // dock a clear theme-adapted shade everywhere.
        backgroundColor: "hsl(var(--paper-2))",
        borderTop: "1px solid hsl(var(--ink))",
        boxShadow: "0 -8px 24px rgba(0,0,0,0.08)",
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        alignItems: "end",
        gap: 6,
        zIndex: 50,
      }}
    >
      {items.map((it) => {
        const isCenter = it.to === "/post";
        const active = it.matches?.(pathname) ?? false;
        // When there are unseen matches, deep-link the Grails tab straight
        // to the matches section so the badge feels like a real inbox.
        const target =
          it.to === "/grails" && grailUnseen > 0 ? "/grails?focus=matches" : it.to;

        if (isCenter) {
          return (
            <button
              key={it.to}
              onClick={() => nav(it.to)}
              aria-label={it.label}
              data-tour="dock-scan"
              style={{
                justifySelf: "center",
                marginTop: -22,
                width: 56, height: 56, borderRadius: "50%",
                background: "hsl(var(--ink))",
                color: "hsl(var(--cream))",
                border: "1px solid hsl(var(--ink))",
                boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
                position: "relative",
              }}
            >
              {it.icon(24)}
            </button>
          );
        }

        return (
          <button
            key={it.to}
            onClick={() => nav(target)}
            aria-label={it.label}
            aria-current={active ? "page" : undefined}
            data-tour={`dock-${it.label.toLowerCase()}`}
            style={{
              background: "transparent",
              border: "none",
              padding: "6px 0 2px",
              display: "flex", flexDirection: "column",
              alignItems: "center", gap: 2,
              cursor: "pointer",
              color: active ? "hsl(var(--dock-accent))" : "hsl(var(--ink-2))",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              position: "relative",
            }}
          >
            {it.icon(20)}
            <span>{it.label}</span>
            {active && (
              <span
                style={{
                  position: "absolute", top: 0, left: "50%",
                  transform: "translateX(-50%)",
                  width: 18, height: 2, background: "hsl(var(--dock-accent))",
                }}
              />
            )}
            {it.to === "/grails" && grailUnseen > 0 && (
              <span
                aria-label={`${grailUnseen} new grail matches`}
                style={{
                  position: "absolute",
                  top: 2,
                  right: "calc(50% - 18px)",
                  minWidth: 16,
                  height: 16,
                  padding: "0 4px",
                  borderRadius: 8,
                  background: "hsl(var(--rust))",
                  color: "hsl(var(--cream))",
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 9,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              >
                {grailUnseen > 9 ? "9+" : grailUnseen}
              </span>
            )}
            {it.to === "/you" && inboxUnread > 0 && (
              <span
                aria-label={`${inboxUnread} unread messages`}
                style={{
                  position: "absolute",
                  top: 2,
                  right: "calc(50% - 18px)",
                  minWidth: 16,
                  height: 16,
                  padding: "0 4px",
                  borderRadius: 8,
                  background: "hsl(var(--marigold))",
                  color: "hsl(var(--marigold-fg))",
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 9,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              >
                {inboxUnread > 9 ? "9+" : inboxUnread}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
